/**
 * ReactFirstAppCoordinator.ts
 * Version React-first de AppCoordinator
 * Remplace AppOrchestrator + AppCoordinator legacy
 * Ne dépend pas de CanvasManager legacy - utilise uniquement le store Zustand
 */
import { logger } from '../utils/ProfessionalLogger';
import { HistoryManager } from '../HistoryManager';
import { FontManager } from '../FontManager';
import { LayerManager } from '../LayerManager';
import { CloudManager, type ProjectData } from '../CloudManager';
import { TextToolService } from './TextToolService';
import { TextStyleManager } from '../managers/TextStyleManager';
import { useAppStore } from './StateService';
import { UnifiedSaveService } from './UnifiedSaveService';
import { validateProjectData } from '../types/ProjectData';
import { FileUploadService } from './FileUploadService';
import type { ICanvasManager } from '../types/manager-interfaces';
import { type ShapeType, createDefaultShape } from '../types/ShapeTypes';
import Konva from 'konva';

export interface ReactFirstAppCoordinatorConfig {
  historyManager: HistoryManager;
  fontManager: FontManager;
  layerManager: LayerManager;
  cloudManager: CloudManager;
  defaultWidth: number;
  defaultHeight: number;
}

/**
 * AppCoordinator React-first
 * Expose les mêmes méthodes que AppCoordinator legacy mais sans dépendre de CanvasManager
 */
export class ReactFirstAppCoordinator {
  private historyManager: HistoryManager;
  private fontManager: FontManager;
  private layerManager: LayerManager;
  private cloudManager: CloudManager;
  private textStyleManager: TextStyleManager;
  private textToolService: TextToolService;

  constructor(config: ReactFirstAppCoordinatorConfig) {
    this.historyManager = config.historyManager;
    this.fontManager = config.fontManager;
    this.layerManager = config.layerManager;
    this.cloudManager = config.cloudManager;

    // Initialiser les services texte
    this.textStyleManager = new TextStyleManager();
    // NOTE: TextToolService nécessite CanvasManager mais on crée un stub minimal
    // Les méthodes qui nécessitent CanvasManager seront adaptées pour utiliser le store Zustand
    const canvasManagerStub: ICanvasManager = {
      getSelectedNode: () => null,
      getSelectedNodes: () => [],
      selectNode: () => {},
      selectNodes: () => {},
      deselectAll: () => {},
      getLayer: () => {
        // Stub: retourne un layer minimal (non utilisé en React-first)
        return new Konva.Layer();
      },
      getStage: () => {
        // Stub: retourne un stage minimal (non utilisé en React-first)
        return new Konva.Stage({ container: document.createElement('div') });
      },
      addRectangle: () => new Konva.Rect(),
      addCircle: () => new Konva.Circle(),
      addText: () => new Konva.Text({ text: '' }),
      addImage: async () => new Konva.Image({ image: undefined }),
      addPhotoSpace: () => new Konva.Group(),
      deleteSelected: () => {},
      duplicateSelected: () => null,
      updateSelectedNode: () => {},
      moveSelectedUp: () => {},
      moveSelectedDown: () => {},
      moveSelectedToTop: () => {},
      moveSelectedToBottom: () => {},
      setDimensions: () => {},
      setBackgroundColor: () => {},
      setBackgroundImage: async () => {},
      toJSON: () => '{}',
      loadFromJSON: () => {},
      exportToImage: () => '',
      exportToPNG: () => '',
      notifyStateChange: () => {},
    };

    this.textToolService = new TextToolService({
      canvasManager: canvasManagerStub,
      textStyleManager: this.textStyleManager,
    });

    logger.info('✅ ReactFirstAppCoordinator initialisé');
  }

  /**
   * Getters pour compatibilité avec l'API legacy
   */
  public getHistoryManager(): HistoryManager {
    return this.historyManager;
  }

  public getFontManager(): FontManager {
    return this.fontManager;
  }

  public getLayerManager(): LayerManager {
    return this.layerManager;
  }

  public getCloudManager(): CloudManager {
    return this.cloudManager;
  }

  public getTextToolService(): TextToolService {
    return this.textToolService;
  }

  public getTextStyleManager(): TextStyleManager {
    return this.textStyleManager;
  }

  /**
   * STUB: getCanvasManager() pour compatibilité
   * Retourne null car on n'utilise plus CanvasManager legacy
   * Les composants React doivent utiliser le store Zustand directement
   */
  public getCanvasManager(): null {
    logger.warn(
      '⚠️ getCanvasManager() appelé - CanvasManager legacy non disponible en mode React-first'
    );
    logger.warn('⚠️ Utilisez le store Zustand (useAppStore) directement dans les composants React');
    return null;
  }

  /**
   * PR-06: Méthode exportProject supprimée
   * ExportModal est maintenant React-first et utilise stageRef directement
   * Plus besoin de passer par le coordinator pour l'export
   */

  /**
   * Charge un projet depuis un fichier ou des données
   * Utilise le store Zustand comme source unique de vérité
   */
  public async loadProject(projectDataOrFile?: Record<string, unknown> | File): Promise<void> {
    try {
      // Si c'est un File, le lire d'abord
      if (projectDataOrFile instanceof File) {
        const fileService = FileUploadService.getInstance();
        const jsonString = await fileService.readFileAsText(projectDataOrFile);
        const parsed = JSON.parse(jsonString);
        return this.loadProject(parsed);
      }

      // Nettoyer la sélection avant de charger (évite les zombie transformers)
      const store = useAppStore.getState();
      store.clearSelection();

      // Parser les données du projet
      let projectData: ProjectData;
      if (typeof projectDataOrFile === 'string') {
        const parsed = JSON.parse(projectDataOrFile);
        // Valider avec le schéma Zod
        const validation = validateProjectData(JSON.stringify(parsed));
        if (!validation.success) {
          throw new Error(`Données de projet invalides: ${validation.error}`);
        }
        projectData = validation.data;
      } else if (projectDataOrFile && typeof projectDataOrFile === 'object') {
        // Valider avec le schéma Zod
        const validation = validateProjectData(JSON.stringify(projectDataOrFile));
        if (!validation.success) {
          throw new Error(`Données de projet invalides: ${validation.error}`);
        }
        projectData = validation.data;
      } else {
        throw new Error('Données de projet invalides');
      }

      // Si le projet a une structure ProjectData (depuis CloudManager).
      // Le validateur Zod retourne un union large : on accède via une vue locale
      // typée pour éviter 18 TS2339 dus à la verbosité du type union.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const projectAny = projectData as any;
      if (projectAny.data) {
        const data = projectAny.data;

        // Charger dans StateService (source unique de vérité)
        if (data.shapes) {
          store.loadShapes(data.shapes);
        }
        if (data.canvas) {
          if (data.canvas.templateWidth && data.canvas.templateHeight) {
            store.setTemplateDimensions(data.canvas.templateWidth, data.canvas.templateHeight);
          }
          if (data.canvas.backgroundColor) {
            store.setBackgroundColor(data.canvas.backgroundColor);
          }
          if (data.canvas.documentName) {
            store.setDocumentName(data.canvas.documentName);
          }
        }
        if (data.project) {
          if (data.project.name) {
            store.setProjectName(data.project.name);
          }
          if (data.project.format) {
            store.setProjectFormat(data.project.format);
          }
          if (data.project.dpi) {
            store.setProjectDpi(data.project.dpi);
          }
        }
      } else if (projectAny.shapes) {
        // Format legacy : données directes
        store.loadShapes(projectAny.shapes);
      }

      // Enregistrer dans l'historique
      const currentState = useAppStore.getState();
      this.historyManager.saveState({
        shapes: currentState.shapes,
        canvas: currentState.canvas,
        project: currentState.project,
      });

      logger.info('✅ Projet chargé (React-first, synchronisé avec StateService)');
    } catch (error) {
      logger.error('Erreur lors du chargement du projet:', error);
      throw error;
    }
  }

  // Note (P2 cleanup) : ancienne surcharge `handleExternalDrop(e, stage, containerRect)`
  // retirée — la seule version vivante est plus bas (cf. ligne définissant
  // `handleExternalDrop(e, stage)` qui lit `application/snapbooth-{type,payload}`).
  // Le helper `inferShapeTypeFromElement` n'était utilisé que par cette ancienne
  // surcharge, supprimé avec elle.

  /**
   * Sauvegarde le projet
   * Utilise UnifiedSaveService pour sauvegarder dans le cloud et localStorage
   * P0.2 FIX: Met à jour le store avec l'ID généré pour éviter de régénérer un UUID à chaque sauvegarde
   * @param downloadFile - Si true, télécharge aussi un fichier JSON
   * @param asNew - Si true, crée un nouveau projet (génère un nouvel ID)
   */
  public async saveProject(downloadFile: boolean = false, asNew: boolean = false): Promise<void> {
    try {
      // Récupérer les données depuis StateService (source unique de vérité)
      const store = useAppStore.getState();

      // Mettre à jour le statut de sauvegarde pour l'UI
      logger.info('💾 Début de la sauvegarde...');
      store.setSaveStatus('saving'); // Affiche "Sauvegarde..." dans l'UI

      const projectName = store.canvas.documentName || store.project.name || 'projet-snapbooth';

      // Générer un UUID valide si l'ID n'existe pas
      const generateUUID = (): string => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
          return crypto.randomUUID();
        }
        // Fallback pour les navigateurs qui ne supportent pas crypto.randomUUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
      };

      // P0.2 FIX: Générer l'ID si nécessaire et le mettre à jour dans le store
      let projectId = store.project.id;
      if (!projectId || asNew) {
        projectId = generateUUID();
        // Mettre à jour le store avec l'ID généré pour éviter de régénérer à chaque sauvegarde
        store.setProjectId(projectId);
        logger.info(
          `📝 ${asNew ? 'Nouveau projet créé' : 'Nouveau projet créé'} avec ID: ${projectId}`
        );
      }

      // Construire l'objet projet complet depuis StateService
      const projectData = {
        id: projectId,
        name: projectName,
        data: {
          shapes: store.shapes,
          canvas: store.canvas,
          project: store.project,
          selection: store.selection,
        },
        createdAt: store.project.createdAt?.getTime() || Date.now(),
        updatedAt: Date.now(),
        version: '1.0.0',
      };

      // Sauvegarder dans CloudManager (backend ou localStorage selon le mode)
      // Utiliser UnifiedSaveService pour la sauvegarde
      const unifiedSaveService = UnifiedSaveService.getInstance();
      await unifiedSaveService.save({
        projectId: projectData.id,
        saveToCloud: true,
        saveToLocal: true,
        priority: 'high',
      });

      // Télécharger un fichier JSON uniquement si demandé explicitement (sauvegarde manuelle)
      if (downloadFile) {
        const jsonString = JSON.stringify(projectData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${projectName}-${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        logger.info(
          '✅ Projet sauvegardé (React-first: backend/localStorage + fichier JSON téléchargé)'
        );
      } else {
        logger.info('✅ Projet sauvegardé (React-first: backend/localStorage)');
      }

      // Mettre à jour le statut de sauvegarde pour l'UI
      store.setSaveStatus('saved'); // Affiche "Sauvegardé"
      logger.info('✅ Sauvegarde réussie !');
    } catch (error) {
      logger.error('❌ Erreur de sauvegarde :', error);
      const store = useAppStore.getState();
      store.setSaveStatus('error');
      throw error;
    }
  }

  /**
   * Gère le drop d'un élément externe (depuis ElementsDrawer) sur le canvas
   * Calcule la position relative au stage Konva et crée la shape via le store Zustand
   */
  public handleExternalDrop(e: React.DragEvent | DragEvent, stage: Konva.Stage): void {
    e.preventDefault();

    // 1. Lire les données depuis dataTransfer
    const type = e.dataTransfer?.getData('application/snapbooth-type');
    const payload = e.dataTransfer?.getData('application/snapbooth-payload');

    if (!type) {
      logger.warn(
        '[ReactFirstAppCoordinator] handleExternalDrop: aucun type trouvé dans dataTransfer'
      );
      return;
    }

    // 2. Calculer la position relative au stage (crucial pour que l'objet apparaisse sous la souris)
    stage.setPointersPositions(e);
    const pointer = stage.getRelativePointerPosition();

    if (!pointer) {
      logger.warn(
        '[ReactFirstAppCoordinator] handleExternalDrop: impossible de calculer la position du pointeur'
      );
      return;
    }

    // 3. Ajuster la position en tenant compte de l'offset du canvas (50, 50)
    const canvasX = pointer.x - 50;
    const canvasY = pointer.y - 50;

    // 4. Appeler le StateService (Zustand) pour créer la shape
    const store = useAppStore.getState();

    if (type === 'image' && payload) {
      // Logique pour les images
      const shape = createDefaultShape('image', canvasX, canvasY);
      shape.src = payload;
      shape.width = 200; // Défaut
      shape.height = 200; // Défaut
      shape.id = crypto.randomUUID();
      shape.rotation = 0;
      shape.opacity = 1;
      shape.visible = true;
      shape.locked = false;
      shape.zIndex = 99;

      store.addShape(shape);
      logger.info(`[ReactFirstAppCoordinator] Image ajoutée depuis drop: ${payload}`);
    } else {
      // Logique pour les formes simples
      const shapeType = type as ShapeType;
      const shape = createDefaultShape(shapeType, canvasX, canvasY);
      shape.width = 100;
      shape.height = 100;
      shape.fill = '#cccccc';
      shape.stroke = '#000000';
      shape.id = crypto.randomUUID();
      shape.rotation = 0;
      shape.opacity = 1;
      shape.visible = true;
      shape.locked = false;
      shape.zIndex = 99;

      store.addShape(shape);
      logger.info(`[ReactFirstAppCoordinator] Shape ${shapeType} ajoutée depuis drop`);
    }
  }

  /**
   * Lance l'exportation du projet
   * Met à jour le store pour indiquer que l'export est en cours
   * La logique d'export est gérée par ExportModal via stageRef
   */
  public async exportProject(): Promise<void> {
    const store = useAppStore.getState();
    logger.info("📤 Démarrage de l'export...");

    // Ouvre la modale d'exportation via le store
    store.setExporting(true);

    // Note : La logique réelle de génération d'image (html2canvas ou Konva.toDataURL)
    // doit être gérée par le composant qui écoute "isExporting" ou via un service dédié.
  }

  /**
   * Gestion de l'Undo (Bouton "Retour")
   */
  public undo(): void {
    const store = useAppStore.getState();
    // Vérifie si la fonction undo existe dans votre store (middleware temporal)
    if (store.undo) {
      store.undo();
      logger.info('⏪ Undo effectué');
    } else {
      logger.warn('⚠️ Middleware Undo/Redo non configuré dans Zustand');
    }
  }

  /**
   * Gestion du Redo (Bouton "Avancer")
   */
  public redo(): void {
    const store = useAppStore.getState();
    if (store.redo) {
      store.redo();
      logger.info('⏩ Redo effectué');
    } else {
      logger.warn('⚠️ Middleware Undo/Redo non configuré dans Zustand');
    }
  }
}
